<template>
  <SoDialog
    v-model="value"
    :loading="configurationUpdateMutator.isPending"
    card-title="Are you sure?"
    data-testid="configurations-modal"
  >
    <SoErrorBoundary>
      <SoAlert
        type="warning"
        title="You are about to make the following changes:"
      />
      <SoSimpleTable
        :headers="['Configuration', 'Value']"
        :items="
          configurations.map((configuration: Configuration) => [
            configuration.name,
            configuration.value,
          ])
        "
        class="mt-4 mb-2 border rounded"
        data-testid="configurations-modal-table"
      />
    </SoErrorBoundary>
    <SoAlert
      v-if="configurationUpdateMutator.isSuccess"
      title="Changes saved"
      type="success"
      class="mt-4"
      data-testid="configurations-modal-success-alert"
    />
    <SoAlert
      v-if="configurationUpdateMutator.isError"
      title="Failed to save changes"
      type="error"
      class="mt-4"
      data-testid="configurations-modal-error-alert"
    />
    <SoCheckbox
      v-if="isProduction && !configurationUpdateMutator.data"
      v-model="confirmed"
      label="I understand these changes will affect our customers"
      class="mt-6"
      data-testid="configurations-modal-confirm-checkbox"
    />
    <div class="d-flex justify-end mt-8">
      <template v-if="configurationUpdateMutator.data">
        <SoButton
          title="Close"
          large
          data-testid="configurations-modal-close-btn"
          @click="value = false"
        />
      </template>
      <template v-else>
        <SoButton
          title="Cancel"
          class="mr-2"
          large
          data-testid="configurations-modal-cancel-btn"
          @click="value = false"
        />
        <SoButton
          title="Save"
          type="success"
          large
          :disabled="isProduction && !confirmed"
          data-testid="configurations-modal-save-btn"
          @click="saveChanges()"
        />
      </template>
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { isProduction } from "@/helpers/envHelper"
  import {
    Configuration_Updates,
    UpdateConfigurationsMutationVariables,
  } from "@/api/hasura/generated/graphql"
  import { Configuration } from "@/models/admin/configuration/configuration"
  import { ConfigurationMutator } from "@/mutators/configuration/ConfigurationMutator"

  export default defineComponent({
    components: {
      SoDialog,
      SoErrorBoundary,
      SoSimpleTable,
      SoButton,
      SoCheckbox,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      configurations: {
        type: Array as PropType<Configuration[]>,
        default: null,
      },
    },
    emits: ["update:modelValue", "success"],
    data() {
      return {
        confirmed: false,
        configurationUpdateMutator: ConfigurationMutator.update(),
      }
    },
    computed: {
      isProduction,
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
    },
    watch: {
      value(val) {
        if (!val) {
          if (this.configurationUpdateMutator.isSuccess) {
            this.$emit("success")
            this.configurationUpdateMutator.reset()
          }
          this.confirmed = false
        }
      },
    },
    methods: {
      saveChanges() {
        this.configurationUpdateMutator.mutate(
          this.formatMutationVariables(this.configurations),
        )
      },
      formatMutationVariables(configurations: Configuration[]) {
        const formattedVariables = {
          updates: [],
        } as UpdateConfigurationsMutationVariables
        configurations.forEach((configuration) =>
          (formattedVariables.updates as Configuration_Updates[]).push({
            where: { id: { _eq: configuration.id } },
            _set: { value: String(configuration.value) },
          }),
        )
        return formattedVariables
      },
    },
  })
</script>
